export function getClientTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function convertToLocalTimezone(date: string | number | Date): Date {
  return new Date(Date.parse(String(date)) - new Date().getTimezoneOffset() * 60000);
}

export function parseUtcDate(value: string | number | Date): Date {
  if (value instanceof Date) return value;
  const dateString = String(value).trim().replace(' ', 'T');
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateString)) return new Date(dateString);
  return new Date(`${dateString}Z`);
}

/**
 * Test a string to check if it is a string representation of a date or datetime object
 */
export function isDateString(value: unknown): boolean {
  // Regular expression to match ISO 8601 datetime and simple dates
  const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;
  if (typeof value === 'string' && dateRegex.test(value)) return true;
  return false;
}

export function calculateBackwardRelativeDate(number: number, unit: string): Date {
  const date = new Date();
  switch (unit) {
    case 'days':
      date.setDate(date.getDate() - number);
      break;
    case 'months':
      date.setMonth(date.getMonth() - number);
      break;
    case 'years':
      date.setFullYear(date.getFullYear() - number);
      break;
    default:
      throw new Error('Invalid date unit');
  }
  return date;
}

/**
 * Returns a parsed JSON string while also handling dates and datetimes
 */
export function parseJSONAndAutoConvertDates(jsonString: string): unknown {
  function convertDates(obj: Record<string, unknown>): void {
    for (const key in obj) {
      if (isDateString(obj[key])) {
        obj[key] = new Date(obj[key] as string);
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        convertDates(obj[key] as Record<string, unknown>);
      }
    }
  }

  const data = JSON.parse(jsonString) as Record<string, unknown>;
  convertDates(data);
  return data;
}

/**
 * Formats a Date object as a string in the format "YYYY-MM-DD".
 */
export function formatDateISO(date: Date | null | undefined): string | null {
  if (!date || isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // +1 because getMonth() returns 0-11
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function normalizeDatetimeLocal(value: string): string {
  const dateSplitted = value.split('T');
  if (dateSplitted.length !== 2) return value;

  const datePart = dateSplitted[0];
  const timePart = dateSplitted[1];

  // datetime-local is timezone-less and represents local time.
  // Keep local time as-is and ensure seconds are present.
  const timeMatch = timePart.match(/^(\d{2}:\d{2})(?::(\d{2}))?$/);
  if (!timeMatch) return value;

  const hoursMinutes = timeMatch[1];
  const seconds = timeMatch[2];

  return seconds ? value : `${datePart}T${hoursMinutes}:00`;
}

export function formatClockTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function formatTime(seconds: number): string {
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const sec = Math.floor(seconds % 60);

  let timeString = '';
  if (days > 0) timeString += `${days}d `;
  if (hours > 0 || days > 0) timeString += `${hours}h `;
  if (minutes > 0 || hours > 0 || days > 0) timeString += `${minutes}m `;
  if (sec > 0) timeString += `${sec}s`;

  return timeString;
}

export function reverseDate(dateString: string): string {
  const [day, month, year] = dateString.split('/');
  return `${year}/${month}/${day}`;
}

/**
 * Parses a date range object to extract the 'from' and 'to' dates as formatted strings.
 */
export function parseDateRange(dateRange: { from?: unknown; to?: unknown } | null | undefined): {
  fromDate: string | null;
  toDate: string | null;
} {
  try {
    // Handle null dateRange case, which is "all_time"
    if (!dateRange?.from || !dateRange?.to) return { fromDate: null, toDate: null };
    // we need to convert the date to UTC to avoid timezone issues
    let fromDate: Date | string | null = new Date(dateRange.from as string | number | Date);
    let gmtDate = new Date(fromDate.toUTCString());
    fromDate.setDate(gmtDate.getDate());
    fromDate = formatDateISO(fromDate);

    // Do the same for the to date
    let toDate: Date | string | null = new Date(dateRange.to as string | number | Date);
    gmtDate = new Date(toDate.toUTCString());
    toDate.setDate(gmtDate.getDate() + 1);
    toDate = formatDateISO(toDate);
    return { fromDate, toDate };
  } catch (error) {
    console.error('error parsing date range', error);
    return { fromDate: null, toDate: null };
  }
}

export function toDateString(date: Date | null | undefined): string | undefined {
  return date?.toISOString?.().split('T')[0];
}
